import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
    Mail, 
    Send, 
    Upload, 
    Users, 
    Eye,
    EyeOff,
    CheckCircle2,
    Clock,
    AlertCircle,
    Trash2,
    FileText,
    BarChart3,
    RefreshCw,
    ChevronDown,
    Edit3,
    BookUser,
    UserCheck,
    Tag
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Template options
const TEMPLATE_OPTIONS = [
    { 
        id: 'invitation', 
        name: 'Invitation Letter', 
        description: 'Invite R&R Saturday attendees to join the membership',
        subjectDefault: "You're Invited to Join the Recovery Room Community"
    },
    { 
        id: 'welcome', 
        name: 'Welcome Email', 
        description: 'Welcome new members to the Recovery Room community',
        subjectDefault: "Welcome to Recovery Room, Brother!"
    },
    { 
        id: 'custom', 
        name: 'Custom Message', 
        description: 'Write your own plain text message',
        subjectDefault: "A Message from Recovery Room"
    }
];

const BulkEmailPage = () => {
    const [emailList, setEmailList] = useState('');
    const [parsedEmails, setParsedEmails] = useState([]);
    const [templateType, setTemplateType] = useState('invitation');
    const [subject, setSubject] = useState("You're Invited to Join the Recovery Room Community");
    const [customBody, setCustomBody] = useState('');
    const [sending, setSending] = useState(false);
    const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0 });
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showPreview, setShowPreview] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    
    // Contact & Member Selection
    const [recipientSource, setRecipientSource] = useState('manual'); // manual, contacts, members, both
    const [contacts, setContacts] = useState([]);
    const [contactTags, setContactTags] = useState([]);
    const [selectedContactTag, setSelectedContactTag] = useState('');
    const [members, setMembers] = useState([]);
    const [includeMembers, setIncludeMembers] = useState(false);
    const [contactStats, setContactStats] = useState(null);

    useEffect(() => {
        fetchCampaigns();
        fetchContacts();
        fetchContactTags();
        fetchMembers();
        fetchContactStats();
    }, []);

    useEffect(() => {
        // Re-fetch contacts when tag filter changes
        fetchContacts();
    }, [selectedContactTag]);

    const fetchCampaigns = async () => {
        try {
            const response = await axios.get(`${API}/admin/email-campaigns`);
            setCampaigns(response.data);
        } catch (error) {
            console.error('Failed to fetch campaigns:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchContacts = async () => {
        try {
            const params = selectedContactTag ? `?tag=${selectedContactTag}` : '';
            const response = await axios.get(`${API}/admin/contacts${params}`);
            setContacts(response.data);
        } catch (error) {
            console.error('Failed to fetch contacts:', error);
        }
    };

    const fetchContactTags = async () => {
        try {
            const response = await axios.get(`${API}/admin/contacts/tags`);
            setContactTags(response.data);
        } catch (error) {
            console.error('Failed to fetch contact tags:', error);
        }
    };

    const fetchMembers = async () => {
        try {
            const response = await axios.get(`${API}/members`);
            setMembers(response.data.filter(m => m.email)); // Only members with emails
        } catch (error) {
            console.error('Failed to fetch members:', error);
        }
    };

    const fetchContactStats = async () => {
        try {
            const response = await axios.get(`${API}/admin/contacts/stats`);
            setContactStats(response.data);
        } catch (error) {
            console.error('Failed to fetch contact stats:', error);
        }
    };

    const parseEmails = (text) => {
        // Parse emails from text (comma, newline, or space separated)
        const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
        const matches = text.match(emailRegex) || [];
        const uniqueEmails = [...new Set(matches.map(e => e.toLowerCase()))];
        setParsedEmails(uniqueEmails);
        return uniqueEmails;
    };

    const handleEmailListChange = (e) => {
        const text = e.target.value;
        setEmailList(text);
        parseEmails(text);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const text = event.target.result;
                setEmailList(text);
                parseEmails(text);
            };
            reader.readAsText(file);
        }
    };

    const handleTemplateChange = (newTemplateType) => {
        setTemplateType(newTemplateType);
        const template = TEMPLATE_OPTIONS.find(t => t.id === newTemplateType);
        if (template) {
            setSubject(template.subjectDefault);
        }
        // Clear custom body when switching away from custom
        if (newTemplateType !== 'custom') {
            setCustomBody('');
        }
    };

    // Compile all recipient emails based on selected sources
    const getRecipientEmails = () => {
        let emails = [];
        
        if (recipientSource === 'manual') {
            emails = [...parsedEmails];
        } else if (recipientSource === 'contacts') {
            emails = contacts.map(c => c.email);
        } else if (recipientSource === 'members') {
            emails = members.map(m => m.email);
        } else if (recipientSource === 'both') {
            emails = [...contacts.map(c => c.email), ...members.map(m => m.email)];
        }
        
        // If includeMembers is checked (for manual or contacts mode), add member emails
        if (includeMembers && recipientSource !== 'members' && recipientSource !== 'both') {
            emails = [...emails, ...members.map(m => m.email)];
        }
        
        // Deduplicate
        return [...new Set(emails.map(e => e.toLowerCase()))];
    };

    const getTotalRecipientCount = () => {
        return getRecipientEmails().length;
    };

    const handleSendCampaign = async () => {
        const finalEmails = getRecipientEmails();
        
        if (finalEmails.length === 0) {
            toast.error('Please add at least one email address');
            return;
        }

        if (!subject.trim()) {
            toast.error('Please enter a subject line');
            return;
        }

        if (templateType === 'custom' && !customBody.trim()) {
            toast.error('Please enter a message body for custom emails');
            return;
        }

        setSending(true);
        setSendProgress({ sent: 0, total: finalEmails.length });

        try {
            const response = await axios.post(`${API}/admin/send-bulk-email`, {
                emails: finalEmails,
                subject: subject,
                template_type: templateType,
                custom_body: templateType === 'custom' ? customBody : null
            });

            if (response.data.success) {
                toast.success(`Campaign sent to ${response.data.sent_count} recipients!`);
                setEmailList('');
                setParsedEmails([]);
                fetchCampaigns();
            } else {
                toast.error(response.data.message || 'Failed to send campaign');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send campaign');
        } finally {
            setSending(false);
            setSendProgress({ sent: 0, total: 0 });
        }
    };

    const removeEmail = (emailToRemove) => {
        const newEmails = parsedEmails.filter(e => e !== emailToRemove);
        setParsedEmails(newEmails);
        setEmailList(newEmails.join('\n'));
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getOpenRate = (campaign) => {
        if (!campaign.total_sent || campaign.total_sent === 0) return 0;
        return Math.round((campaign.total_opened / campaign.total_sent) * 100);
    };

    return (
        <div className="space-y-6" data-testid="bulk-email-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-[#3D4435] font-['Playfair_Display']">
                        Bulk Email Campaigns
                    </h1>
                    <p className="text-gray-500 mt-1">
                        Send branded invitation emails to potential members
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={() => setShowPreview(!showPreview)}
                    className="gap-2"
                >
                    {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {showPreview ? 'Hide Preview' : 'Show Template'}
                </Button>
            </div>

            {/* Template Preview */}
            {showPreview && (
                <Card className="border-[#C9A227]">
                    <CardHeader className="bg-[#3D4435] text-white rounded-t-lg">
                        <CardTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-[#C9A227]" />
                            Email Template Preview: {TEMPLATE_OPTIONS.find(t => t.id === templateType)?.name}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {templateType === 'custom' ? (
                            <div className="p-6 bg-[#3D4435]">
                                <div className="max-w-[600px] mx-auto bg-[#3D4435] border border-[#5A6350] rounded-lg overflow-hidden">
                                    {/* Header */}
                                    <div className="bg-gradient-to-br from-[#3D4435] to-[#2D3428] p-8 text-center border-b-4 border-[#C9A227]">
                                        <img 
                                            src="https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/mnjq1cak_Recovery%20Room%20Archive-07.png" 
                                            alt="Recovery Room" 
                                            className="h-12 mx-auto mb-3"
                                        />
                                        <h1 className="text-[#C9A227] text-2xl font-serif">Recovery Room</h1>
                                        <p className="text-[#A0A0A0] text-xs tracking-widest mt-1">BROTHERHOOD. GROWTH. PURPOSE.</p>
                                    </div>
                                    {/* Body */}
                                    <div className="p-8">
                                        <p className="text-white text-lg mb-4">Greetings, Brother</p>
                                        <div className="text-[#A0A0A0] leading-relaxed whitespace-pre-wrap">
                                            {customBody || '(Your custom message will appear here)'}
                                        </div>
                                        <p className="text-white mt-8">
                                            In Brotherhood,<br/>
                                            <strong className="text-[#C9A227]">The Recovery Room Team</strong>
                                        </p>
                                    </div>
                                    {/* Footer */}
                                    <div className="bg-[#2D3428] p-4 text-center border-t border-[#5A6350]">
                                        <p className="text-[#666666] text-xs">Recovery Room • A Safe Space for Black Men</p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <iframe 
                                src={templateType === 'welcome' ? '/email-preview-welcome.html' : '/email-preview.html'} 
                                className="w-full h-[600px] border-0"
                                title="Email Template Preview"
                            />
                        )}
                    </CardContent>
                </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Compose Section */}
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-[#3D4435]">
                                <Mail className="w-5 h-5 text-[#C9A227]" />
                                Compose Campaign
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Template Selector */}
                            <div className="space-y-2">
                                <Label>Email Template</Label>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {TEMPLATE_OPTIONS.map((template) => (
                                        <button
                                            key={template.id}
                                            onClick={() => handleTemplateChange(template.id)}
                                            className={`p-4 rounded-lg border-2 text-left transition-all ${
                                                templateType === template.id
                                                    ? 'border-[#C9A227] bg-[#C9A227]/10'
                                                    : 'border-gray-200 hover:border-[#C9A227]/50'
                                            }`}
                                            data-testid={`template-${template.id}`}
                                        >
                                            <div className="flex items-center gap-2 mb-1">
                                                {template.id === 'invitation' && <Mail className="w-4 h-4 text-[#C9A227]" />}
                                                {template.id === 'welcome' && <CheckCircle2 className="w-4 h-4 text-[#C9A227]" />}
                                                {template.id === 'custom' && <Edit3 className="w-4 h-4 text-[#C9A227]" />}
                                                <span className={`font-medium ${templateType === template.id ? 'text-[#3D4435]' : 'text-gray-700'}`}>
                                                    {template.name}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-500">{template.description}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Subject Line */}
                            <div className="space-y-2">
                                <Label>Subject Line</Label>
                                <Input
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="Enter email subject..."
                                    className="text-base"
                                    data-testid="email-subject-input"
                                />
                            </div>

                            {/* Custom Body (only for custom template) */}
                            {templateType === 'custom' && (
                                <div className="space-y-2">
                                    <Label>Message Body (Plain Text)</Label>
                                    <textarea
                                        value={customBody}
                                        onChange={(e) => setCustomBody(e.target.value)}
                                        placeholder="Write your message here. Line breaks will be preserved..."
                                        className="w-full h-48 p-3 border rounded-lg resize-none focus:ring-2 focus:ring-[#C9A227] focus:border-transparent"
                                        data-testid="custom-body-input"
                                    />
                                    <p className="text-xs text-gray-500">
                                        Your message will be wrapped in the Recovery Room branded email template with header and footer.
                                    </p>
                                </div>
                            )}

                            {/* Recipient Source Selection */}
                            <div className="space-y-3">
                                <Label>Select Recipients</Label>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    <button
                                        onClick={() => setRecipientSource('manual')}
                                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                                            recipientSource === 'manual'
                                                ? 'border-[#C9A227] bg-[#C9A227]/10'
                                                : 'border-gray-200 hover:border-[#C9A227]/50'
                                        }`}
                                    >
                                        <Upload className="w-5 h-5 mx-auto mb-1 text-[#C9A227]" />
                                        <span className="text-sm font-medium">Manual/Upload</span>
                                    </button>
                                    <button
                                        onClick={() => setRecipientSource('contacts')}
                                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                                            recipientSource === 'contacts'
                                                ? 'border-[#C9A227] bg-[#C9A227]/10'
                                                : 'border-gray-200 hover:border-[#C9A227]/50'
                                        }`}
                                    >
                                        <BookUser className="w-5 h-5 mx-auto mb-1 text-[#C9A227]" />
                                        <span className="text-sm font-medium">Contacts</span>
                                        <Badge className="ml-1 text-xs">{contacts.length}</Badge>
                                    </button>
                                    <button
                                        onClick={() => setRecipientSource('members')}
                                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                                            recipientSource === 'members'
                                                ? 'border-[#C9A227] bg-[#C9A227]/10'
                                                : 'border-gray-200 hover:border-[#C9A227]/50'
                                        }`}
                                    >
                                        <UserCheck className="w-5 h-5 mx-auto mb-1 text-[#C9A227]" />
                                        <span className="text-sm font-medium">Members</span>
                                        <Badge className="ml-1 text-xs">{members.length}</Badge>
                                    </button>
                                    <button
                                        onClick={() => setRecipientSource('both')}
                                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                                            recipientSource === 'both'
                                                ? 'border-[#C9A227] bg-[#C9A227]/10'
                                                : 'border-gray-200 hover:border-[#C9A227]/50'
                                        }`}
                                    >
                                        <Users className="w-5 h-5 mx-auto mb-1 text-[#C9A227]" />
                                        <span className="text-sm font-medium">All</span>
                                        <Badge className="ml-1 text-xs">{contactStats?.total_reachable || 0}</Badge>
                                    </button>
                                </div>
                            </div>

                            {/* Manual Email Input (only for manual source) */}
                            {recipientSource === 'manual' && (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label>Email Addresses</Label>
                                        <label className="cursor-pointer">
                                            <input
                                                type="file"
                                                accept=".csv,.txt"
                                                onChange={handleFileUpload}
                                                className="hidden"
                                            />
                                            <span className="inline-flex items-center gap-1 text-sm text-[#C9A227] hover:underline">
                                                <Upload className="w-4 h-4" />
                                                Upload CSV/TXT
                                            </span>
                                        </label>
                                    </div>
                                    <textarea
                                        value={emailList}
                                        onChange={handleEmailListChange}
                                        placeholder="Paste email addresses here (comma, newline, or space separated)..."
                                        className="w-full h-40 p-3 border rounded-lg resize-none focus:ring-2 focus:ring-[#C9A227] focus:border-transparent"
                                        data-testid="email-list-input"
                                    />
                                    
                                    {/* Include Members Checkbox */}
                                    <label className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                                        <input
                                            type="checkbox"
                                            checked={includeMembers}
                                            onChange={(e) => setIncludeMembers(e.target.checked)}
                                            className="w-4 h-4 text-[#C9A227] rounded"
                                        />
                                        <span className="text-sm">Also include all registered members ({members.length})</span>
                                    </label>
                                </div>
                            )}

                            {/* Contact Tag Filter (for contacts source) */}
                            {recipientSource === 'contacts' && (
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Tag className="w-4 h-4" />
                                        Filter by Tag
                                    </Label>
                                    <div className="flex gap-2 flex-wrap">
                                        <Button
                                            variant={selectedContactTag === '' ? 'default' : 'outline'}
                                            size="sm"
                                            onClick={() => setSelectedContactTag('')}
                                            className={selectedContactTag === '' ? 'bg-[#3D4435]' : ''}
                                        >
                                            All Contacts ({contactStats?.total_contacts || 0})
                                        </Button>
                                        {contactTags.map((t) => (
                                            <Button
                                                key={t.tag}
                                                variant={selectedContactTag === t.tag ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setSelectedContactTag(t.tag)}
                                                className={selectedContactTag === t.tag ? 'bg-[#C9A227]' : ''}
                                            >
                                                {t.tag} ({t.count})
                                            </Button>
                                        ))}
                                    </div>
                                    
                                    {/* Include Members Checkbox */}
                                    <label className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 mt-3">
                                        <input
                                            type="checkbox"
                                            checked={includeMembers}
                                            onChange={(e) => setIncludeMembers(e.target.checked)}
                                            className="w-4 h-4 text-[#C9A227] rounded"
                                        />
                                        <span className="text-sm">Also include all registered members ({members.length})</span>
                                    </label>
                                </div>
                            )}

                            {/* Recipient Summary */}
                            <div className="p-4 bg-[#3D4435]/5 rounded-lg border border-[#3D4435]/20">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Mail className="w-5 h-5 text-[#C9A227]" />
                                        <span className="font-medium text-[#3D4435]">
                                            {getTotalRecipientCount()} Total Recipients
                                        </span>
                                    </div>
                                    {recipientSource !== 'manual' && (
                                        <span className="text-sm text-gray-500">
                                            {recipientSource === 'contacts' && `${contacts.length} contacts`}
                                            {recipientSource === 'members' && `${members.length} members`}
                                            {recipientSource === 'both' && `${contacts.length} contacts + ${members.length} members`}
                                            {includeMembers && recipientSource !== 'members' && recipientSource !== 'both' && ` + ${members.length} members`}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Parsed Emails Preview (only for manual) */}
                            {recipientSource === 'manual' && parsedEmails.length > 0 && (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label className="flex items-center gap-2">
                                            <Users className="w-4 h-4 text-[#C9A227]" />
                                            {parsedEmails.length} Manual Recipients
                                        </Label>
                                        <button
                                            onClick={() => { setEmailList(''); setParsedEmails([]); }}
                                            className="text-sm text-red-500 hover:underline"
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                    <div className="max-h-40 overflow-y-auto p-3 bg-gray-50 rounded-lg space-y-1">
                                        {parsedEmails.slice(0, 20).map((email, index) => (
                                            <div 
                                                key={index}
                                                className="flex items-center justify-between text-sm py-1 px-2 bg-white rounded hover:bg-gray-100"
                                            >
                                                <span>{email}</span>
                                                <button
                                                    onClick={() => removeEmail(email)}
                                                    className="text-gray-400 hover:text-red-500"
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ))}
                                        {parsedEmails.length > 20 && (
                                            <p className="text-sm text-gray-500 text-center py-2">
                                                ... and {parsedEmails.length - 20} more
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Send Button */}
                            <Button
                                onClick={handleSendCampaign}
                                disabled={sending || getTotalRecipientCount() === 0}
                                className="w-full bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold py-6"
                                data-testid="send-campaign-btn"
                            >
                                {sending ? (
                                    <>
                                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                                        Sending... ({sendProgress.sent}/{sendProgress.total})
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-5 h-5 mr-2" />
                                        Send to {getTotalRecipientCount()} Recipients
                                    </>
                                )}
                            </Button>
                        </CardContent>
                    </Card>
                </div>

                {/* Stats Sidebar */}
                <div className="space-y-6">
                    {/* Quick Stats */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-[#3D4435]">
                                <BarChart3 className="w-5 h-5 text-[#C9A227]" />
                                Campaign Stats
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                                    <span className="text-sm text-gray-600">Total Sent</span>
                                </div>
                                <span className="font-bold text-green-600">
                                    {campaigns.reduce((acc, c) => acc + (c.total_sent || 0), 0)}
                                </span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <Eye className="w-5 h-5 text-blue-600" />
                                    <span className="text-sm text-gray-600">Total Opened</span>
                                </div>
                                <span className="font-bold text-blue-600">
                                    {campaigns.reduce((acc, c) => acc + (c.total_opened || 0), 0)}
                                </span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5 text-purple-600" />
                                    <span className="text-sm text-gray-600">Avg Open Rate</span>
                                </div>
                                <span className="font-bold text-purple-600">
                                    {campaigns.length > 0 
                                        ? Math.round(campaigns.reduce((acc, c) => acc + getOpenRate(c), 0) / campaigns.length)
                                        : 0}%
                                </span>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Info Card */}
                    <Card className="bg-[#3D4435] text-white">
                        <CardContent className="p-4">
                            <h3 className="font-semibold text-[#C9A227] mb-2">
                                {templateType === 'invitation' && 'About Invitation Template'}
                                {templateType === 'welcome' && 'About Welcome Template'}
                                {templateType === 'custom' && 'About Custom Messages'}
                            </h3>
                            <p className="text-sm text-gray-300 leading-relaxed">
                                {templateType === 'invitation' && 
                                    'This invitation email is designed for R&R Saturday attendees and Eventbrite registrants. It highlights membership benefits and includes a direct link to create a profile.'
                                }
                                {templateType === 'welcome' && 
                                    'This welcome email is sent to new members who have just created their profile. It guides them through their first steps in the community.'
                                }
                                {templateType === 'custom' && 
                                    'Write a custom plain-text message that will be wrapped in the Recovery Room branded email template. Perfect for announcements and one-off communications.'
                                }
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Campaign History */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2 text-[#3D4435]">
                            <Clock className="w-5 h-5 text-[#C9A227]" />
                            Campaign History
                        </CardTitle>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={fetchCampaigns}
                            className="gap-1"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Refresh
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="text-center py-8 text-gray-500">Loading campaigns...</div>
                    ) : campaigns.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p>No campaigns sent yet</p>
                            <p className="text-sm">Your sent campaigns will appear here</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Date</th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Subject</th>
                                        <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Template</th>
                                        <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Sent</th>
                                        <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Opened</th>
                                        <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Open Rate</th>
                                        <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {campaigns.map((campaign) => (
                                        <tr 
                                            key={campaign.id} 
                                            className="border-b hover:bg-gray-50 cursor-pointer"
                                            onClick={() => setSelectedCampaign(campaign)}
                                        >
                                            <td className="py-3 px-4 text-sm">
                                                {formatDate(campaign.created_at)}
                                            </td>
                                            <td className="py-3 px-4 text-sm font-medium">
                                                {campaign.subject}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <Badge className={
                                                    campaign.template_type === 'invitation' ? 'bg-amber-100 text-amber-700' :
                                                    campaign.template_type === 'welcome' ? 'bg-green-100 text-green-700' :
                                                    'bg-purple-100 text-purple-700'
                                                }>
                                                    {campaign.template_type === 'invitation' ? 'Invitation' :
                                                     campaign.template_type === 'welcome' ? 'Welcome' :
                                                     campaign.template_type === 'custom' ? 'Custom' :
                                                     campaign.template_type || 'Invitation'}
                                                </Badge>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <Badge variant="secondary">
                                                    {campaign.total_sent}
                                                </Badge>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <Badge className="bg-blue-100 text-blue-700">
                                                    {campaign.total_opened}
                                                </Badge>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <span className={`font-semibold ${
                                                    getOpenRate(campaign) >= 30 ? 'text-green-600' :
                                                    getOpenRate(campaign) >= 15 ? 'text-yellow-600' : 'text-red-600'
                                                }`}>
                                                    {getOpenRate(campaign)}%
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <Badge className={
                                                    campaign.status === 'completed' 
                                                        ? 'bg-green-100 text-green-700' 
                                                        : 'bg-yellow-100 text-yellow-700'
                                                }>
                                                    {campaign.status}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Campaign Details Modal */}
            {selectedCampaign && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedCampaign(null)}>
                    <Card className="w-full max-w-2xl max-h-[80vh] overflow-auto m-4" onClick={e => e.stopPropagation()}>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle>Campaign Details</CardTitle>
                                <button onClick={() => setSelectedCampaign(null)} className="text-gray-400 hover:text-gray-600">
                                    ✕
                                </button>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <Label className="text-gray-500">Subject</Label>
                                <p className="font-medium">{selectedCampaign.subject}</p>
                            </div>
                            <div>
                                <Label className="text-gray-500">Sent</Label>
                                <p>{formatDate(selectedCampaign.created_at)}</p>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="p-4 bg-gray-50 rounded-lg text-center">
                                    <p className="text-2xl font-bold text-[#3D4435]">{selectedCampaign.total_sent}</p>
                                    <p className="text-sm text-gray-500">Sent</p>
                                </div>
                                <div className="p-4 bg-blue-50 rounded-lg text-center">
                                    <p className="text-2xl font-bold text-blue-600">{selectedCampaign.total_opened}</p>
                                    <p className="text-sm text-gray-500">Opened</p>
                                </div>
                                <div className="p-4 bg-green-50 rounded-lg text-center">
                                    <p className="text-2xl font-bold text-green-600">{getOpenRate(selectedCampaign)}%</p>
                                    <p className="text-sm text-gray-500">Open Rate</p>
                                </div>
                            </div>
                            
                            {/* Recipients List */}
                            <div>
                                <Label className="text-gray-500 mb-2 block">Recipients ({selectedCampaign.recipients?.length || 0})</Label>
                                <div className="max-h-60 overflow-y-auto bg-gray-50 rounded-lg p-3 space-y-1">
                                    {selectedCampaign.recipients?.map((recipient, idx) => (
                                        <div key={idx} className="flex items-center justify-between text-sm py-1 px-2 bg-white rounded">
                                            <span>{recipient.email}</span>
                                            <Badge className={recipient.opened ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                                                {recipient.opened ? 'Opened' : 'Not opened'}
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default BulkEmailPage;
